import { parseISO } from 'date-fns';
import type { TreeData } from '@/shared/ui';
import type { GetPlanningTreeResponse } from '@/api/generated/schemas';
import {
  buildVenuaPlannerTypeDefinitions,
  type VenuaPlannerTreeTypeLabels,
} from './venuaPlannerTreeShared';

export type VenuaPlannerEntityType = 'project' | 'product' | 'serie' | 'term';

export const VENUA_PLANNER_ENTITY_TYPES = [
  'project',
  'product',
  'serie',
  'term',
] as const satisfies readonly VenuaPlannerEntityType[];

/**
 * Stavba stromu plánovací agendy nad read modelem `GetPlanningTree`.
 *
 * Vrací jen strukturu (`nodesById`/`rootIds`) + sdílené `typeDefinitions`. Akce řádků
 * (`actionsByType`) i aktivaci uzlu dodává stránka přes `venuaPlannerTreeShared`, protože jde o
 * sdílené navigační události společné s kalendářovou agendou.
 */
export function buildVenuaPlannerTreeData(
  response: GetPlanningTreeResponse,
  labels: VenuaPlannerTreeTypeLabels
): TreeData {
  const nodesById: TreeData['nodesById'] = {};
  const rootIds: string[] = [];

  for (const project of response.projects) {
    rootIds.push(project.projectId);
    nodesById[project.projectId] = {
      id: project.projectId,
      label: project.name,
      type: 'project',
      parentId: null,
      childIds: project.products.map((product) => product.productId),
    };

    for (const product of project.products) {
      nodesById[product.productId] = {
        id: product.productId,
        label: product.name,
        type: 'product',
        parentId: project.projectId,
        childIds: product.series.map((serie) => serie.productSerieId),
      };

      for (const serie of product.series) {
        nodesById[serie.productSerieId] = {
          id: serie.productSerieId,
          label: serie.name,
          type: 'serie',
          parentId: product.productId,
          childIds: serie.terms.map((term) => term.termId),
        };

        for (const term of serie.terms) {
          nodesById[term.termId] = {
            id: term.termId,
            label: term.name,
            type: 'term',
            parentId: serie.productSerieId,
            childIds: [],
          };
        }
      }
    }
  }

  return {
    nodesById,
    rootIds,
    typeDefinitions: buildVenuaPlannerTypeDefinitions(labels),
  };
}

/** Všechna term id ve stromu (výchozí „vše zaškrtnuto“ + dohledání nově vzniklých termínů) */
export function collectTreeTermIds(response: GetPlanningTreeResponse): string[] {
  return response.projects.flatMap((project) =>
    project.products.flatMap((product) =>
      product.series.flatMap((serie) => serie.terms.map((term) => term.termId))
    )
  );
}

/**
 * Mapa `termId → první den termínu` z odpovědi stromu. Slouží k odscrollování na termín po
 * kliknutí ve stromu (cílový měsíc lze určit i pro zatím nenačtené měsíce). Termíny bez
 * `dateFrom` se vynechají.
 */
export function collectTreeTermDates(response: GetPlanningTreeResponse): Map<string, Date> {
  const map = new Map<string, Date>();
  for (const project of response.projects) {
    for (const product of project.products) {
      for (const serie of product.series) {
        for (const term of serie.terms) {
          if (term.dateFrom) map.set(term.termId, parseISO(term.dateFrom));
        }
      }
    }
  }
  return map;
}

/** Všechna term id v podstromu uzlu (uzel typu `term` vrací sám sebe). */
function collectSubtreeTermIds(treeData: TreeData, nodeId: string): string[] {
  const node = treeData.nodesById[nodeId];
  if (!node) return [];
  if (node.type === 'term') return [nodeId];
  return node.childIds.flatMap((childId) => collectSubtreeTermIds(treeData, childId));
}

/**
 * Nejbližší termín pod uzlem — cíl skoku v kalendáři po kliknutí na řádek.
 *
 * Vybírá první nadcházející termín (`dateFrom >= now`), jinak poslední proběhlý. Přednost mají
 * zaškrtnuté (v kalendáři viditelné) termíny; když pod uzlem žádný zaškrtnutý není, vrátí nejbližší
 * ze všech a volající ho zaškrtne. Uzel typu `term` vrací sám sebe, i když je skrytý.
 */
export function findNearestTermId(
  treeData: TreeData,
  nodeId: string,
  termDates: ReadonlyMap<string, Date>,
  checkedTermIds: ReadonlySet<string>,
  now: Date
): string | null {
  const candidates = collectSubtreeTermIds(treeData, nodeId).filter((id) => termDates.has(id));
  if (candidates.length === 0) return null;

  const checked = candidates.filter((id) => checkedTermIds.has(id));
  const pool = checked.length > 0 ? checked : candidates;
  const nowMs = now.getTime();

  let upcoming: { id: string; ms: number } | null = null;
  let past: { id: string; ms: number } | null = null;
  for (const id of pool) {
    const ms = termDates.get(id)!.getTime();
    if (ms >= nowMs) {
      if (!upcoming || ms < upcoming.ms) upcoming = { id, ms };
    } else if (!past || ms > past.ms) {
      past = { id, ms };
    }
  }
  return (upcoming ?? past)?.id ?? null;
}

/**
 * Vrátí seznam ID předků daného uzlu v pořadí od nejbližšího rodiče k rootu.
 * Použití: programatický `expandToNode` po refreshi stromu nebo po vytvoření nového uzlu.
 */
export function getAncestorIds(treeData: TreeData, nodeId: string): string[] {
  const ancestors: string[] = [];
  let current = treeData.nodesById[nodeId];
  while (current?.parentId) {
    ancestors.push(current.parentId);
    current = treeData.nodesById[current.parentId];
  }
  return ancestors;
}

/**
 * `expandToNode` — sjednotí množinu rozbalených uzlů s předky cílového uzlu,
 * aby zůstal viditelný i po programatickém setActive.
 */
export function expandToNode(
  treeData: TreeData,
  nodeId: string,
  baseExpanded: Iterable<string> = []
): Set<string> {
  const next = new Set(baseExpanded);
  for (const ancestorId of getAncestorIds(treeData, nodeId)) {
    next.add(ancestorId);
  }
  return next;
}

/**
 * `setActive` — vrátí trojici (active, expanded) pro programatickou aktivaci.
 * Volající ji propaguje do agenda state (activeNodeId) a do TreeView (defaultExpandedIds).
 */
export function setActive(
  treeData: TreeData,
  nodeId: string,
  baseExpanded: Iterable<string> = []
): { activeNodeId: string; expandedIds: Set<string> } {
  return {
    activeNodeId: nodeId,
    expandedIds: expandToNode(treeData, nodeId, baseExpanded),
  };
}
